/**
 * Tablas de `SkillLine` (contrato de `@scout/core`).
 *
 * Columnas: Tot / # / = / Ef% / Pos% — las mismas que la planilla imprimible
 * del API, para que el papel y la pantalla no discutan. `perfectPct` en
 * recepción es POSITIVIDAD (#+), y así se rotula.
 */
import { Fragment } from 'react';
import type { SkillLine } from '@scout/core';
import type { TFunction } from '../i18n/index';
import { useT } from '../i18n/use-t';
import { effPct, pct, playerLabel, skillLabel, sortSkills } from '../lib/format';

function SkillRow({ skill, line, t }: { skill: string; line: SkillLine; t: TFunction }): JSX.Element {
  return (
    <tr>
      <td>{skillLabel(skill, t)}</td>
      <td className="num">{line.total}</td>
      <td className="num">{line.byEvaluation['#'] ?? 0}</td>
      <td className="num">{line.byEvaluation['='] ?? 0}</td>
      <td className={`num ${line.efficiency < 0 ? 'neg' : ''}`}>{effPct(line.efficiency)}</td>
      <td className="num">{pct(line.perfectPct)}</td>
    </tr>
  );
}

export function TeamSkillsTable({
  skills,
}: {
  skills: Partial<Record<string, SkillLine>>;
}): JSX.Element {
  const t = useT();
  const keys = sortSkills(Object.keys(skills));
  if (keys.length === 0) return <p className="empty">{t('skills.empty')}</p>;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t('skills.skill')}</th>
            <th className="num">{t('skills.total')}</th>
            <th className="num" title={t('skills.perfectTitle')}>
              {t('skills.perfect')}
            </th>
            <th className="num" title={t('skills.errorsTitle')}>
              {t('skills.errors')}
            </th>
            <th className="num" title={t('skills.efficiencyTitle')}>
              {t('skills.efficiency')}
            </th>
            <th className="num" title={t('skills.positivityTitle')}>
              {t('skills.positivity')}
            </th>
          </tr>
        </thead>
        <tbody>
          {keys.map((skill) => {
            const line = skills[skill];
            return line ? <SkillRow key={skill} skill={skill} line={line} t={t} /> : null;
          })}
        </tbody>
      </table>
    </div>
  );
}

export interface PlayerRow {
  number: number;
  name?: string;
  skills: Partial<Record<string, SkillLine>>;
  pointsScored: number;
  /** Sólo en temporada. */
  matches?: number;
}

/**
 * Tabla de jugadores: una fila por jugador y, por fundamento visible, el
 * total y la eficiencia. Se muestran sólo los fundamentos con datos, así no
 * quedan diez columnas vacías en un partido a medio codificar.
 */
export function PlayersTable({
  players,
  showMatches = false,
}: {
  players: readonly PlayerRow[];
  showMatches?: boolean;
}): JSX.Element {
  const t = useT();
  if (players.length === 0) return <p className="empty">{t('skills.noPlayers')}</p>;

  const present = new Set<string>();
  for (const p of players) for (const skill of Object.keys(p.skills)) present.add(skill);
  const skills = sortSkills(present);

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t('common.player')}</th>
            {showMatches ? <th className="num">{t('skills.matchesPlayed')}</th> : null}
            <th className="num" title={t('skills.pointsTitle')}>
              {t('skills.points')}
            </th>
            {skills.map((skill) => (
              <th key={skill} className="num" colSpan={2}>
                {skillLabel(skill, t)}
              </th>
            ))}
          </tr>
          <tr>
            <th />
            {showMatches ? <th /> : null}
            <th />
            {skills.map((skill) => (
              <Fragment key={skill}>
                <th className="num">{t('skills.total')}</th>
                <th className="num">{t('skills.efficiency')}</th>
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {[...players]
            .sort((a, b) => a.number - b.number)
            .map((player) => (
              <tr key={`${player.number}-${player.name ?? ''}`}>
                <td>{playerLabel(player.number, player.name)}</td>
                {showMatches ? <td className="num">{player.matches ?? 0}</td> : null}
                <td className="num">{player.pointsScored}</td>
                {skills.map((skill) => {
                  const line = player.skills[skill];
                  return (
                    <Fragment key={skill}>
                      <td className="num">{line ? line.total : '·'}</td>
                      <td className={`num ${line && line.efficiency < 0 ? 'neg' : ''}`}>
                        {line && line.total > 0 ? effPct(line.efficiency) : '·'}
                      </td>
                    </Fragment>
                  );
                })}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}
